import React, { useState } from 'react';
import { ShieldCheck, Globe, Info, Check, Search } from 'lucide-react';

// ── Static mockup v2 — per-functionality layout ──
// Left: functionality list (grouped). Right: choose "Common (all lines)" or
// "Specific business line(s)". If Specific → pick the line(s). Non-functional preview.

const BUSINESS_LINES = [
  { id: 'bl1', label: 'FASTag' },
  { id: 'bl2', label: 'Prepaid Cards' },
  { id: 'bl3', label: 'Banking (Hitch Zone)' },
  { id: 'bl4', label: 'Dealership' },
];

const CATALOG = [
  { group: 'Common', features: [['org-tree', 'Org Tree'], ['mobile-lookup', 'Mobile Lookup'], ['team', 'Team Management']] },
  { group: 'Field Activity', features: [['field-activity', 'Overview'], ['visits', 'Visits'], ['travel-plans', 'Travel Plans']] },
  { group: 'FASTag', features: [['revenue', 'FASTag Onboarding'], ['margin', 'Margin Management'], ['performance', 'Performance'], ['payout', 'FASTag Payout']] },
  { group: 'Hitch Zone', features: [['banking', 'Hitch Zone Txns']] },
  { group: 'Prepaid Cards', features: [['prepaid-onboarding', 'Prepaid Card Onboarding'], ['prepaid-card', 'Dispatch Report']] },
  { group: 'Operations', features: [['approvals', 'Approvals'], ['expense-claims', 'Expense Claims'], ['leads', 'Leads'], ['txn-audit', 'Txn Auditor']] },
];

// links[key] = [] means COMMON (irrespective). A non-empty array = specific line ids.
const DEFAULTS = {
  revenue: ['bl1'], margin: ['bl1'], performance: ['bl1'], payout: ['bl1'],
  banking: ['bl3'], 'prepaid-onboarding': ['bl2'], 'prepaid-card': ['bl2'],
};

function BizPill({ on, label, onClick }) {
  return (
    <button onClick={onClick}
      className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${on ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-slate-500 border-slate-200 hover:border-amber-300'}`}>
      {on && <Check className="w-3 h-3 inline mr-0.5 -mt-0.5" />}{label}
    </button>
  );
}

export default function BusinessLineAccessPreview() {
  const [links, setLinks] = useState(() => {
    const m = {};
    CATALOG.forEach(g => g.features.forEach(([k]) => { m[k] = DEFAULTS[k] ? [...DEFAULTS[k]] : []; }));
    return m;
  });
  const [q, setQ] = useState('');

  const setSpecific = (key, specific) => setLinks(prev => ({ ...prev, [key]: specific ? (prev[key].length ? prev[key] : [BUSINESS_LINES[0].id]) : [] }));
  const toggleLine = (key, blId) => setLinks(prev => {
    const cur = new Set(prev[key]);
    cur.has(blId) ? cur.delete(blId) : cur.add(blId);
    return { ...prev, [key]: [...cur] };
  });

  const match = (label) => label.toLowerCase().includes(q.toLowerCase());
  const specificCount = Object.values(links).filter(a => a.length).length;
  const commonCount = Object.values(links).filter(a => !a.length).length;

  return (
    <div className="min-h-screen bg-slate-100 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-amber-600" /> Business Line Access
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            For each functionality choose <b>Common</b> (visible to every business line) or <b>Specific</b>
            (visible only to the chosen line(s)). Common functionalities are business-line irrespective.
            Enforced everywhere — the mapping screen and the sidebar.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search functionality…"
              className="w-full h-9 text-sm border border-slate-300 rounded-md pl-8 pr-2 bg-white" />
          </div>
          <span className="text-[11px] text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1">
            <b className="text-emerald-600">{commonCount}</b> common · <b className="text-amber-600">{specificCount}</b> line-specific
          </span>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* header row */}
          <div className="grid grid-cols-[1.1fr_2fr] gap-2 px-4 py-2.5 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
            <span>Functionality</span>
            <span>Visibility — Common or Specific business line(s)</span>
          </div>
          {CATALOG.map(g => {
            const rows = g.features.filter(([, l]) => match(l));
            if (!rows.length) return null;
            return (
              <div key={g.group}>
                <div className="px-4 py-1.5 bg-slate-50/60 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{g.group}</div>
                {rows.map(([k, label]) => {
                  const specific = links[k].length > 0;
                  return (
                    <div key={k} className="grid grid-cols-[1.1fr_2fr] gap-2 px-4 py-2.5 border-b border-slate-100 items-center hover:bg-slate-50/50">
                      <span className="text-sm text-slate-700 font-medium">{label}</span>
                      <div className="flex flex-wrap items-center gap-2">
                        {/* segmented Common / Specific */}
                        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                          <button onClick={() => setSpecific(k, false)}
                            className={`text-[11px] px-2.5 py-1 font-medium ${!specific ? 'bg-emerald-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            <Globe className="w-3 h-3 inline mr-1 -mt-0.5" />Common
                          </button>
                          <button onClick={() => setSpecific(k, true)}
                            className={`text-[11px] px-2.5 py-1 font-medium border-l border-slate-200 ${specific ? 'bg-amber-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}>
                            Specific
                          </button>
                        </div>
                        {/* if specific → choose lines */}
                        {specific ? (
                          <div className="flex flex-wrap gap-1.5">
                            {BUSINESS_LINES.map(bl => (
                              <BizPill key={bl.id} label={bl.label} on={links[k].includes(bl.id)} onClick={() => toggleLine(k, bl.id)} />
                            ))}
                          </div>
                        ) : (
                          <span className="text-[11px] text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">visible to all lines</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-blue-200 shadow-sm p-4">
          <div className="flex items-start gap-1.5 text-[11px] text-blue-800">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p><b>Common</b> → every user (with the department) sees it, regardless of business line.</p>
              <p><b>Specific</b> → only users whose business line matches one of the chosen line(s) see it.</p>
              <p><b>User with NO business line</b> → sees only the <b>Common</b> functionalities.</p>
            </div>
          </div>
        </div>

        <div className="text-center text-xs text-slate-400">Try toggling Common / Specific and picking lines.</div>
      </div>
    </div>
  );
}
